/**
 * Client-side reorder MARGINAL pricing (reorder-v2.2).
 *
 * The discount applies to each INCREMENTAL pair, never to pairs already at a
 * better position: pair 1 = full preset, pair 2 = -tier2%, pairs 3..N = -tier3%
 * each; total = the sum. Mirrors the server's public.reorder_marginal_total,
 * which sums public.reorder_unit_gross(kind, n) over pairs 1..qty.
 *
 * The wizard passes the SERVER-emitted per-pair prices (view.reorder_price_aed =
 * pair 1, reorder_second_pair_gross_aed = pair 2, reorder_additional_pair_gross_aed
 * = pairs 3+), so what it displays is exactly what submit-reorder charges. The
 * admin preview has only base + percentages and derives the pair prices with the
 * same rounding via reorderMarginalFromPreset.
 */

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** Per-pair gross for a tier % — mirrors public.reorder_unit_gross rounding. */
export function reorderPairUnit(base: number, pct: number): number {
  if (!Number.isFinite(base) || base <= 0) return 0;
  const p = Number.isFinite(pct) ? pct : 0;
  return round2(base * (1 - p / 100));
}

export interface ReorderMarginalPricing {
  /** Per-pair gross list, length = qty: [pair1, pair2, pair3, pair3, ...]. */
  pairs: number[];
  /** Sum of pairs — the gross total actually charged (VAT included). */
  total: number;
  /** Pair-1 gross (full preset). */
  base: number;
  /** base x qty — full price with no volume discount. */
  baseTotal: number;
  /** baseTotal - total — AED saved at this quantity. */
  savings: number;
}

/**
 * Marginal pricing from the three per-pair unit prices (u1 = preset, u2 = 2nd
 * pair, u3 = 3rd-onwards). A missing/invalid discounted price degrades to the
 * base (no phantom discount) rather than throwing.
 */
export function reorderMarginalPricing(
  u1: number | null | undefined,
  u2: number | null | undefined,
  u3: number | null | undefined,
  qty: number,
): ReorderMarginalPricing {
  const base = Number.isFinite(u1) && (u1 as number) > 0 ? round2(u1 as number) : 0;
  const p2 = Number.isFinite(u2) && (u2 as number) > 0 ? round2(u2 as number) : base;
  const p3 = Number.isFinite(u3) && (u3 as number) > 0 ? round2(u3 as number) : base;
  const n = Math.max(0, Math.floor(Number.isFinite(qty) ? qty : 0));
  const pairs: number[] = [];
  for (let i = 1; i <= n; i += 1) pairs.push(i === 1 ? base : i === 2 ? p2 : p3);
  const total = round2(pairs.reduce((a, b) => a + b, 0));
  const baseTotal = round2(base * n);
  return { pairs, total, base, baseTotal, savings: round2(baseTotal - total) };
}

/** Convenience for callers that only have base + tier percentages (admin preview). */
export function reorderMarginalFromPreset(
  base: number,
  tier2Pct: number,
  tier3Pct: number,
  qty: number,
): ReorderMarginalPricing {
  return reorderMarginalPricing(round2(base), reorderPairUnit(base, tier2Pct), reorderPairUnit(base, tier3Pct), qty);
}

/**
 * Reorder cart R1: the applicable cross-sell per-pair gross, chosen from the
 * SERVER-emitted full/discounted prices by the anchor quantity. The server
 * decides the numbers and the threshold (get_patient_public_view /
 * reorder_cart_quote); this only PICKS which server number applies and never
 * derives a discount (a missing discounted price degrades to full).
 *
 * Sandals cross-sold on an insole anchor: full until anchor qty >= 2, then the
 * single hard-capped discount. Insoles cross-sold on a sandal anchor: from = 1
 * so the discounted price applies from the first pair.
 */
export function reorderCrossUnit(
  fullAed: number | null | undefined,
  discountedAed: number | null | undefined,
  appliesFromAnchorQty: number | null | undefined,
  anchorQty: number,
): number {
  const full = Number.isFinite(fullAed) && (fullAed as number) > 0 ? round2(fullAed as number) : 0;
  // No full base price means there is nothing to offer - never fabricate one
  // from the discounted field alone.
  if (full <= 0) return 0;
  const disc =
    Number.isFinite(discountedAed) && (discountedAed as number) > 0 ? round2(discountedAed as number) : full;
  const from = Number.isFinite(appliesFromAnchorQty) ? (appliesFromAnchorQty as number) : 1;
  return anchorQty >= from ? disc : full;
}

/** Cross-sell line gross = applicable unit x cross qty (flat, no ladder). */
export function reorderCrossTotal(unit: number, crossQty: number): number {
  const n = Math.max(0, Math.floor(Number.isFinite(crossQty) ? crossQty : 0));
  const u = Number.isFinite(unit) && unit > 0 ? round2(unit) : 0;
  return round2(u * n);
}

export interface ReorderLadderSegment {
  price: number;
  count: number;
}

/**
 * Collapse consecutive equal per-pair prices for compact display, e.g.
 * [1500,1350,1200,1200] -> [{1500,1},{1350,1},{1200,2}] which the wizard renders
 * as "1,500 + 1,350 + 1,200 x 2 = 5,250".
 */
export function reorderLadderSegments(pairs: number[]): ReorderLadderSegment[] {
  const segs: ReorderLadderSegment[] = [];
  for (const p of pairs) {
    const last = segs[segs.length - 1];
    if (last && last.price === p) last.count += 1;
    else segs.push({ price: p, count: 1 });
  }
  return segs;
}
